import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Check,
  ShieldCheck,
  Motorcycle,
  Car,
  Truck,
  Van,
  Taxi,
  IdentificationCard,
  SpinnerGap,
  PaperPlaneRight,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  INSURANCE_PLANS,
  INSURANCE_TYPES,
  buildWhatsAppLink,
} from "@/data/services";
import { createInsuranceLead } from "@/lib/rescueApi";

const TYPE_ICONS = {
  "2w": Motorcycle,
  auto: Taxi,
  car: Car,
  taxi: Taxi,
  tata_ace: Van,
  bolero_pickup: Truck,
};

const typeLabel = (id) => INSURANCE_TYPES.find((t) => t.id === id)?.label || id;

const fieldClass =
  "bg-transparent border-white/20 text-white placeholder:text-white/35 focus-visible:border-rescue-primary focus-visible:ring-rescue-primary/40 h-12 rounded-sm";

const EMPTY = { name: "", phone: "", insurance_type: "", registration_number: "", plan: "" };

export const Insurance = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const quoteRef = useRef(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const goToQuote = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    quoteRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.insurance_type) {
      toast.error("Please add your name, phone and insurance type.");
      return;
    }
    if (!/^[0-9+\-\s]{7,15}$/.test(form.phone)) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    const waTab = window.open("", "_blank");
    setLoading(true);
    try {
      await createInsuranceLead(form);
      const message =
        `New insurance quote request\n\n` +
        `Name: ${form.name}\n` +
        `Phone: ${form.phone}\n` +
        `Insurance type: ${typeLabel(form.insurance_type)}\n` +
        `RC / Reg No: ${form.registration_number || "-"}\n` +
        `Plan: ${form.plan || "-"}`;
      const waLink = buildWhatsAppLink(message);
      if (waTab) waTab.location.href = waLink;
      else window.open(waLink, "_blank");
      toast.success("Quote request sent! Confirm on WhatsApp — we'll call you back shortly.");
      setForm(EMPTY);
    } catch {
      if (waTab) waTab.close();
      toast.error("Could not send request. Please call +91 98926 48175.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="insurance" className="relative bg-rescue-surface py-24 sm:py-32 border-y border-white/10">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <span className="text-rescue-primary font-semibold uppercase tracking-[0.3em] text-xs flex items-center gap-2">
              <ShieldCheck weight="fill" size={16} /> Instant insurance
            </span>
            <h2 className="font-display uppercase tracking-tighter text-white text-6xl sm:text-7xl lg:text-8xl leading-[0.85] mt-4">
              Covered<br />in minutes.
            </h2>
          </div>
          <p className="max-w-sm text-white/60 text-base leading-relaxed">
            All-vehicle policies issued on the spot with a digital certificate. Pick a vehicle type,
            share your RC / registration number and get an instant quote.
          </p>
        </div>

        {/* Insurance by vehicle type */}
        <div className="mb-6">
          <span className="text-white/40 text-xs uppercase tracking-widest font-semibold">
            Insurance by vehicle type
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-white/10 border border-white/10 mb-16">
          {INSURANCE_TYPES.map((t, i) => {
            const Icon = TYPE_ICONS[t.id] || Car;
            const active = form.insurance_type === t.id;
            return (
              <motion.button
                key={t.id}
                data-testid={`insurance-type-${t.id}`}
                onClick={() => goToQuote({ insurance_type: t.id })}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (i % 6) * 0.06 }}
                className={`group text-left p-6 transition-colors ${
                  active ? "bg-rescue-primary/15" : "bg-rescue-bg hover:bg-white/[0.04]"
                }`}
              >
                <Icon
                  weight="duotone"
                  size={34}
                  className={active ? "text-rescue-primary" : "text-white/80 group-hover:text-rescue-primary transition-colors"}
                />
                <h3 className="font-display uppercase tracking-tight text-white text-xl mt-4 leading-none">
                  {t.label}
                </h3>
                <p className="text-white/55 text-xs mt-2 leading-relaxed">{t.desc}</p>
              </motion.button>
            );
          })}
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {INSURANCE_PLANS.map((p, i) => (
            <motion.div
              key={p.name}
              data-testid={`insurance-plan-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className={`relative flex flex-col p-8 border ${
                p.highlight ? "bg-rescue-primary/10 border-rescue-primary" : "bg-rescue-bg border-white/10"
              }`}
            >
              {p.highlight && (
                <span className="absolute top-0 right-0 bg-rescue-primary text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5">
                  Most chosen
                </span>
              )}
              <h3 className="font-display uppercase tracking-tight text-white text-3xl">{p.name}</h3>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-display text-5xl text-white">{p.price}</span>
                <span className="text-white/50 text-sm mb-1.5">{p.period}</span>
              </div>
              <ul className="mt-6 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-white/75">
                    <Check
                      weight="bold"
                      size={16}
                      className={`mt-0.5 shrink-0 ${p.highlight ? "text-rescue-primary" : "text-white/50"}`}
                    />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`insurance-select-${i}`}
                onClick={() => goToQuote({ plan: p.name })}
                className={`mt-8 w-full py-3.5 rounded-sm font-semibold uppercase tracking-wide text-sm active:scale-95 transition-all ${
                  p.highlight
                    ? "bg-rescue-primary text-white hover:brightness-110"
                    : "border border-white/20 text-white hover:bg-white/10"
                }`}
              >
                Get quote
              </button>
            </motion.div>
          ))}
        </div>

        {/* Quote form with RC / registration number */}
        <div ref={quoteRef} className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-4">
            <h3 className="font-display uppercase tracking-tighter text-white text-4xl sm:text-5xl leading-[0.9]">
              Get your<br />instant quote.
            </h3>
            <p className="text-white/60 text-sm leading-relaxed mt-4 max-w-sm">
              Share your RC / registration number and our team calls you back within minutes with the
              best premium for your vehicle.
            </p>
          </div>

          <form
            data-testid="insurance-quote-form"
            onSubmit={submit}
            className="lg:col-span-8 bg-rescue-bg border border-white/10 p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-5"
          >
            <div className="space-y-2">
              <Label className="text-white/70 text-xs uppercase tracking-widest">Full name *</Label>
              <Input
                data-testid="insurance-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Your name"
                className={fieldClass}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70 text-xs uppercase tracking-widest">Phone *</Label>
              <Input
                data-testid="insurance-phone"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="+91 98765 43210"
                className={fieldClass}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70 text-xs uppercase tracking-widest">Insurance type *</Label>
              <Select value={form.insurance_type} onValueChange={(v) => update("insurance_type", v)}>
                <SelectTrigger data-testid="insurance-type-select" className={fieldClass}>
                  <SelectValue placeholder="Select vehicle type" />
                </SelectTrigger>
                <SelectContent className="bg-rescue-surface border-white/15 text-white">
                  {INSURANCE_TYPES.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`insurance-type-opt-${t.id}`}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-white/70 text-xs uppercase tracking-widest flex items-center gap-1.5">
                <IdentificationCard size={14} /> RC / Registration number
              </Label>
              <Input
                data-testid="insurance-regno"
                value={form.registration_number}
                onChange={(e) => update("registration_number", e.target.value.toUpperCase())}
                placeholder="MH 04 AB 1234"
                className={fieldClass}
              />
            </div>
            <button
              type="submit"
              data-testid="insurance-quote-submit"
              disabled={loading}
              className="sm:col-span-2 inline-flex items-center justify-center gap-2 bg-rescue-primary hover:brightness-110 active:scale-[0.98] disabled:opacity-60 transition-all text-white text-base font-semibold py-4 rounded-sm uppercase tracking-wide"
            >
              {loading ? (
                <>
                  <SpinnerGap className="animate-spin" size={20} /> Sending…
                </>
              ) : (
                <>
                  Get Instant Quote <PaperPlaneRight weight="fill" size={18} />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};
